import { HeartRoundSolidIcon } from "@wags/ui/product-icons.tsx";
import { type CSSProperties, useRef, useState } from "react";
import { createPortal } from "react-dom";

const HEART_COLORS = ["#e64d63", "#ff7288", "#ffabb8"];
const SPILL_DURATION = 1.35;
const SPILL_GRAVITY = 280;

type HeartParticle = {
    color: string;
    style: CSSProperties;
};

type HeartBurst = {
    id: number;
    style: CSSProperties;
    particles: HeartParticle[];
};

function heartParticle(color: string): HeartParticle {
    const riseDistance = (-140 - Math.random() * 130) * SPILL_DURATION;
    const fallDistance = riseDistance + SPILL_GRAVITY * SPILL_DURATION ** 2;
    const slope = riseDistance / fallDistance;
    return {
        color,
        style: {
            "--spill-size": `${8 + Math.random() * 7}px`,
            "--spill-color": color,
            "--spill-x": `${(Math.random() - 0.5) * 210}px`,
            "--spill-y": `${fallDistance}px`,
            "--spill-rotate": `${(Math.random() - 0.5) * 360}deg`,
            "--spill-fall-easing": `cubic-bezier(${1 / 3}, ${slope / 3}, ${2 / 3}, ${(1 + slope) / 3})`,
        } as CSSProperties,
    };
}

export function useHeartSpill() {
    const streak = useRef({
        listingId: "",
        lastClick: 0,
        count: 0,
        saved: false,
    });
    const nextId = useRef(0);
    const [bursts, setBursts] = useState<HeartBurst[]>([]);

    const onToggle = ({
        button,
        listingId,
        saved,
    }: {
        button: HTMLButtonElement;
        listingId: string;
        saved: boolean;
    }) => {
        const now = performance.now();
        const previous = streak.current;
        const count =
            previous.listingId === listingId &&
            now - previous.lastClick < 700 &&
            previous.saved !== saved
                ? previous.count + 1
                : 1;
        streak.current = { listingId, lastClick: now, count, saved };
        if (count <= 5) return;

        const rect = button.getBoundingClientRect();
        const burst = {
            id: nextId.current++,
            style: {
                "--spill-origin-x": `${rect.left + rect.width / 2}px`,
                "--spill-origin-y": `${rect.top + rect.height / 2}px`,
                "--spill-duration": `${SPILL_DURATION}s`,
            } as CSSProperties,
            particles: HEART_COLORS.map(heartParticle),
        };
        setBursts((current) => [...current.slice(-7), burst]);
    };

    const hearts =
        bursts.length > 0
            ? createPortal(
                  bursts.map((burst) => (
                      <span
                          key={burst.id}
                          aria-hidden="true"
                          className="dog-save-heart-burst"
                          style={burst.style}
                          onAnimationEnd={(event) => {
                              if (event.target !== event.currentTarget) return;
                              setBursts((current) =>
                                  current.filter(({ id }) => id !== burst.id),
                              );
                          }}
                      >
                          {burst.particles.map((particle) => (
                              <span
                                  key={particle.color}
                                  className="dog-save-heart-particle"
                                  style={particle.style}
                              >
                                  <HeartRoundSolidIcon className="dog-save-heart-drift size-full" />
                              </span>
                          ))}
                      </span>
                  )),
                  document.body,
              )
            : null;

    return { onToggle, hearts };
}
